import React from 'react'
import { Box, Tooltip, Typography, TableCell } from '@material-ui/core'
import CommentIcon from '@material-ui/icons/Comment'
import { useTheme, Theme } from '@material-ui/core/styles'
import { CheckCircle, Error as ErrorIcon } from '@material-ui/icons'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import TableChartIcon from '@material-ui/icons/TableChart'

import {
    AuditAttributeValue,
    AuditAttribute,
    AuditValueStatus,
    AttributeDataType,
} from '../../types'
import { statusFromValuesMatch } from './auditValueStatus'

interface AuditCellProps {
    attribute: AuditAttribute
    attributeValue: AuditAttributeValue | undefined
    termId: number
    selectedDocumentFile: string
    cellClasses: {
        dataCell: string
        matchedCell: string
        notMatchedCell: string
        missingRentRollCell: string
        missingDocumentCell: string
        cellContent: string
        cellValue: string
        cellIndicators: string
    }
    onCellClick: (
        attributeValue: AuditAttributeValue,
        attributeName: string,
        leaseDocumentFile: string,
        termId: number,
    ) => void
}

const parseCurrencyAmount = (value: string): number | null => {
    const cleaned = value.replace(/[$,]/g, '').trim()
    if (!cleaned) {
        return null
    }

    const amount = Number(cleaned)
    if (!Number.isFinite(amount)) {
        return null
    }

    return amount
}

const formatCurrency = (value: string): string => {
    const amount = parseCurrencyAmount(value)
    if (amount === null) {
        return value.trim().startsWith('$') ? value : `$${value}`
    }

    const cents = Math.round(amount * 100)
    const hasCents = cents % 100 !== 0

    return (cents / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
    })
}

// Helper function to get status icon
const getStatusIcon = (
    status: AuditValueStatus,
    theme: Theme,
): JSX.Element | null => {
    switch (status) {
        case 'MATCHED':
            return (
                <CheckCircle
                    style={{ fontSize: 16, color: theme.palette.success.main }}
                />
            )
        case 'NOT_MATCHED':
            return (
                <ErrorIcon
                    style={{ fontSize: 16, color: theme.palette.error.main }}
                />
            )
        case 'MISSING_RENT_ROLL':
            return (
                <>
                    <PictureAsPdfIcon
                        style={{
                            fontSize: 16,
                            color: theme.palette.warning.dark,
                        }}
                    />
                    <span
                        style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: theme.palette.warning.dark,
                        }}
                    >
                        DOC
                    </span>
                </>
            )
        case 'MISSING_DOCUMENT':
            return (
                <>
                    <TableChartIcon
                        style={{
                            fontSize: 16,
                            color: theme.palette.warning.dark,
                        }}
                    />
                    <span
                        style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: theme.palette.warning.dark,
                        }}
                    >
                        RR
                    </span>
                </>
            )
        default:
            return null
    }
}

// Helper function to format value based on data type
const formatValue = (
    value: string | null,
    dataType: AttributeDataType | null,
): string => {
    if (!value) return '-'

    switch (dataType) {
        case 'CURRENCY':
            return formatCurrency(value)
        case 'DATE':
            // Format date if needed (currently displaying as-is)
            return value
        case 'NUMBER': {
            // Format number with commas
            const num = parseFloat(value)
            if (!isNaN(num)) {
                return num.toLocaleString('en-US')
            }
            return value
        }
        default:
            return value
    }
}

// Helper function to get cell style class based on status
const getCellStyleClass = (
    status: AuditValueStatus,
    cellClasses: AuditCellProps['cellClasses'],
): string => {
    switch (status) {
        case 'MATCHED':
            return cellClasses.matchedCell
        case 'NOT_MATCHED':
            return cellClasses.notMatchedCell
        case 'MISSING_RENT_ROLL':
            return cellClasses.missingRentRollCell
        case 'MISSING_DOCUMENT':
            return cellClasses.missingDocumentCell
        default:
            return ''
    }
}

export const AuditCell: React.FC<AuditCellProps> = ({
    attribute,
    attributeValue,
    termId,
    selectedDocumentFile,
    cellClasses,
    onCellClick,
}) => {
    const theme = useTheme()

    // If no attribute value exists, render empty cell
    if (!attributeValue) {
        return (
            <TableCell
                key={`${termId}-${attribute.id}`}
                className={cellClasses.dataCell}
            >
                -
            </TableCell>
        )
    }

    // If should_extract is false, render plain cell without styling or status indicators
    if (!attribute.should_extract) {
        return (
            <TableCell
                key={`${termId}-${attribute.id}`}
                className={cellClasses.dataCell}
            >
                <Typography className={cellClasses.cellValue}>
                    {formatValue(
                        attributeValue.rent_roll_value,
                        attribute.data_type,
                    )}
                </Typography>
            </TableCell>
        )
    }

    // If both values are null, render plain cell without styling
    if (!attributeValue.rent_roll_value && !attributeValue.document_value) {
        return (
            <TableCell
                key={`${termId}-${attribute.id}`}
                className={cellClasses.dataCell}
            >
                <Typography className={cellClasses.cellValue}>-</Typography>
            </TableCell>
        )
    }

    const status = statusFromValuesMatch(
        attributeValue.rent_roll_value,
        attributeValue.document_value,
        attributeValue.values_match,
    )

    return (
        <TableCell
            key={`${termId}-${attribute.id}`}
            className={`${cellClasses.dataCell} ${getCellStyleClass(
                status,
                cellClasses,
            )}`}
            onClick={() =>
                onCellClick(
                    attributeValue,
                    attribute.name,
                    attributeValue.document_file || selectedDocumentFile,
                    termId,
                )
            }
        >
            <Box className={cellClasses.cellContent}>
                <Typography className={cellClasses.cellValue}>
                    {formatValue(
                        attributeValue.rent_roll_value,
                        attribute.data_type,
                    )}
                </Typography>
                <Box className={cellClasses.cellIndicators}>
                    {attributeValue.review_note && (
                        <Tooltip title={attributeValue.review_note}>
                            <CommentIcon
                                style={{
                                    fontSize: 14,
                                    color: theme.palette.text.secondary,
                                }}
                            />
                        </Tooltip>
                    )}
                    {getStatusIcon(status, theme)}
                </Box>
            </Box>
        </TableCell>
    )
}
